import React, {
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { Button, Offcanvas } from 'reactstrap';
import Icon from '@mdi/react';
import { mdiAccountSearch, mdiPlusCircle, mdiAccountEdit } from '@mdi/js';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/en-gb';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import ViewDropdown from './components/ViewDropdown/ViewDropdown';
import DateSelect from './components/DateSelect/DateSelect';
import { CalendarContext } from '../../../pages/Activity/context/CalendarProvider';
import { EventContext } from '../../../pages/Activity/context/EventProvider';
import TeamCanvasContent from './components/TeamCanvasContent/TeamCanvasContent';
import EventFormCanvasContent from './components/EventFormCanvasContent/EventFormCanvasContent';
import { CustomOffcanvas } from '../../Common';
import { DateUtil } from '../../../utils';

import './BigCalendar.scss';

export function formatEventTitle(event) {
  const subjectNoWrap = (event?.Subject || '').replace(/ /g, '\u00A0');
  const startTime = event?.start ? moment(event.start).format('HH:mm') : '';
  return `${startTime} - ${subjectNoWrap}`;
}

export function formatEventTooltip(event) {
  const startTime = event?.start
    ? moment(event.start).format('DD MMM YYYY, HH:mm')
    : '';
  const endTime = event?.end
    ? moment(event.end).format('DD MMM YYYY, HH:mm')
    : '';
  return `${event?.Subject ?? ''}\nStart: ${startTime}\nEnd: ${endTime}`;
}

// === Custom renderers (kept OUTSIDE the parent component) ===
export function EventCell({ event }) {
  return (
    <div className="rbc-event-inner" title={formatEventTooltip(event)}>
      <span className="rbc-event-text">{formatEventTitle(event)}</span>
    </div>
  );
}

export function EventWrapper({ children }) {
  return <div className="rbc-event-focusable">{children}</div>;
}

moment.locale('en-gb');
const localizer = momentLocalizer(moment);

export default function BigCalendarSection() {
  const { bigCalendarView, selectedDate } = useContext(CalendarContext);
  const {
    eventDetailsMapped,
    selectedEmployee,
    getEventFontColor,
    isSelectedEmployeeUser,
    getEventBackgroundColor,
    getEventBorderColor,
  } = useContext(EventContext);
  const [selectionRange, setSelectionRange] = useState({
    start: null,
    end: null,
    slots: [],
  });
  const [isOffCanvasOpen, setIsOffCanvasOpen] = useState(false);
  const [offCanvasConfig, setOffCanvasConfig] = useState({
    backdrop: true,
    scrollable: true,
    direction: 'end',
  });
  const [isCustomOffcanvasOpen, setIsCustomOffcanvasOpen] = useState(false);
  const [customOffcanvasConfig, setCustomOffcanvasConfig] = useState({
    backdrop: true,
    scrollable: true,
    direction: 'end',
  });

  const [eventContentType, setEventContentType] = useState('team');
  const [eventFormType, setEventFormType] = useState('create');
  const [eventData, setEventData] = useState(null);

  const scrollToEight = useMemo(() => {
    const d = DateUtil.makeTime(8);
    return new Date(d);
  }, [selectedDate, bigCalendarView?.value]);

  // capture click/drag; if month view build full-day range; always store slots
  const handleSlotSelect = useCallback(
    ({ start, end, slots }) => {
      if (!isSelectedEmployeeUser) return;
      let selSlots = slots;
      const selStart = start;
      let selEnd = end;
      if (bigCalendarView?.value === 'month') {
        selEnd = new Date(end.getTime() - 1);
        selStart.setHours(8, 0, 0, 0);
        selEnd.setHours(17, 0, 0, 0);
        const sorted = [...slots].sort((a, b) => a.getTime() - b.getTime());
        selSlots = sorted;
      }

      setSelectionRange({ start: selStart, end: selEnd, slots: selSlots });
      setEventContentType('event');
      setOffCanvasConfig(prev => ({
        ...prev,
        backdrop: true,
        scrollable: true,
      }));
      setIsOffCanvasOpen(true);
      setEventFormType('create');
    },
    [bigCalendarView, isSelectedEmployeeUser],
  );

  // Handle event selection
  const handleEventSelect = event => {
    if (!isSelectedEmployeeUser) return;
    if (event.EventTyp === 'System Event') {
      setEventFormType('view');
    } else {
      setEventFormType('update');
    }
    setEventData(event);
    setEventContentType('event');
    setOffCanvasConfig(prev => ({
      ...prev,
      backdrop: true,
      scrollable: true,
    }));
    setIsOffCanvasOpen(true);
  };

  // Handle team canvas open
  const handleTeamCanvasOpen = () => {
    setCustomOffcanvasConfig({
      backdrop: false,
      scrollable: true,
      direction: 'end',
    });
    setIsCustomOffcanvasOpen(true);
  };

  // Handle offcanvas close
  const handleCanvasClose = () => {
    setIsOffCanvasOpen(false);
    setEventContentType('team');
    setEventFormType('create');
    setEventData(null);
    setSelectionRange({ start: null, end: null, slots: [] });
  };

  const handleEventPropGetter = event => ({
    style: {
      backgroundColor: getEventBackgroundColor(event),
      color: getEventFontColor(event),
      border: `2px solid ${getEventBorderColor(event)}`,
      padding: '2px 4px',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  });

  const handleDayPropsGetter = useCallback(
    date => {
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;

      if (bigCalendarView?.value === 'month') {
        const inRange = selectionRange.slots.some(
          slot =>
            slot.getFullYear() === date.getFullYear() &&
            slot.getMonth() === date.getMonth() &&
            slot.getDate() === date.getDate(),
        );

        if (inRange) {
          return { style: { backgroundColor: '#cce5ff' } }; // Blue wins
        }
      }

      if (isWeekend) {
        return { style: { backgroundColor: 'rgba(208, 213, 221, 0.6)' } };
      }

      return {};
    },
    [selectionRange, bigCalendarView],
  );

  useEffect(() => {
    if (isOffCanvasOpen) {
      setIsCustomOffcanvasOpen(false);
    }
  }, [isOffCanvasOpen]);

  // Handle the slot prop getter to highlight the selected range (for day/week view)
  const handleSlotPropGetter = useCallback(
    date => {
      const dow = date.getDay();
      if (dow === 0 || dow === 6) {
        return {
          style: {
            backgroundColor: 'rgba(208, 213, 221, 0.6)',
          },
        };
      }

      let rangeStyle = {};
      if (bigCalendarView?.value === 'day') {
        const { start, end, slots } = selectionRange;
        const effectiveSlots = slots.filter(
          slot =>
            slot.getTime() >= start.getTime() && slot.getTime() < end.getTime(),
        );

        if (effectiveSlots.some(slot => slot.getTime() === date.getTime())) {
          rangeStyle = { backgroundColor: '#cce5ff' };
        }
      } else if (bigCalendarView?.value !== 'month') {
        const { start, end } = selectionRange;
        if (
          start &&
          end &&
          date.getTime() >= start.getTime() &&
          date.getTime() < end.getTime()
        ) {
          rangeStyle = { backgroundColor: '#cce5ff' };
        }
      }
      return { style: { ...rangeStyle } };
    },
    [selectionRange, bigCalendarView],
  );

  const titleAccessor = event => {
    // replace every space in the Subject with a non-breaking space
    const subjectNoWrap = event.Subject.replace(/ /g, '\u00A0');
    const startTime = event.start ? moment(event.start).format('HH:mm') : '';
    return `${startTime} - ${subjectNoWrap}`;
  };

  // Handle Tooltip accessor for events
  const tooltipAccessor = event => {
    const startTime = event.start
      ? moment(event.start).format('DD MMM YYYY, HH:mm')
      : '';
    const endTime = event.end
      ? moment(event.end).format('DD MMM YYYY, HH:mm')
      : '';
    return `${event.Subject}\nStart: ${startTime}\nEnd: ${endTime}`;
  };

  // Handle Add event button click
  const handleAddEventButtonClick = () => {
    setIsCustomOffcanvasOpen(false);
    setSelectionRange({ start: null, end: null, slots: [] });
    setEventContentType('event');
    setEventFormType('create');
    setIsOffCanvasOpen(true);
  };

  // Handle generate canvas content based on event type
  const generateCanvasContent = () => {
    if (eventContentType === 'team') {
      return (
        <TeamCanvasContent toggle={() => setIsCustomOffcanvasOpen(false)} />
      );
    }
    if (eventContentType === 'event') {
      return (
        <EventFormCanvasContent
          data={selectionRange}
          type={eventFormType}
          setOffcanvasOpen={setIsOffCanvasOpen}
          eventData={eventData}
          handleCanvasClose={handleCanvasClose}
        />
      );
    }
    return null;
  };

  return (
    <div className="big-calendar-wrapper">
      <div className="d-flex justify-content-between align-items-center gap-2 mb-2 mt-2 px-2 big-calendar-section">
        <div className="d-flex align-items-center gap-2">
          <DateSelect />
          <span className="selected-employee ms-4">
            <span>{selectedEmployee?.employeeName}</span>
            {!isSelectedEmployeeUser && (
              <span
                className="cursor-pointer"
                onClick={() => {
                  handleTeamCanvasOpen();
                }}
              >
                <Icon path={mdiAccountEdit} size={0.8} />
              </span>
            )}
          </span>
        </div>
        <div className="d-flex align-items-center gap-2">
          <ViewDropdown />
          <Button
            className="custom-btn d-flex align-items-center justify-content-center gap-1"
            onClick={handleTeamCanvasOpen}
          >
            <span>Team</span>
            <Icon path={mdiAccountSearch} size={0.7} />
          </Button>
          <Button
            className="custom-btn d-flex align-items-center justify-content-center gap-1"
            onClick={handleAddEventButtonClick}
          >
            <span>Add Event</span>
            <Icon path={mdiPlusCircle} size={0.7} />
          </Button>
        </div>
      </div>
      <div className="big-calendar-content">
        <Calendar
          key={`${bigCalendarView?.value}-${moment(selectedDate).format('YYYY-MM-DD')}`}
          localizer={localizer}
          date={selectedDate}
          events={eventDetailsMapped}
          view={bigCalendarView?.value || 'month'}
          startAccessor="start"
          endAccessor="end"
          titleAccessor={titleAccessor}
          tooltipAccessor={tooltipAccessor}
          eventPropGetter={handleEventPropGetter}
          selectable
          onSelectSlot={handleSlotSelect}
          onSelectEvent={handleEventSelect}
          dayPropGetter={handleDayPropsGetter}
          slotPropGetter={handleSlotPropGetter}
          popup
          style={{ height: '73vh' }}
          toolbar={false}
          // ============= New ======================
          showMultiDayTimes
          step={15}
          timeslots={4}
          // ============= New(20082025) ============
          scrollToTime={scrollToEight}
          // ============= New(28082025) ============
          components={{
            week: { event: EventCell },
            day: { event: EventCell },
            eventWrapper: EventWrapper,
          }}
        />
      </div>
      <Offcanvas
        isOpen={isOffCanvasOpen}
        toggle={handleCanvasClose}
        style={{ width: eventContentType === 'event' ? '40vw' : '30vw' }}
        {...offCanvasConfig}
      >
        {generateCanvasContent()}
      </Offcanvas>
      <CustomOffcanvas
        isOpen={isCustomOffcanvasOpen}
        toggle={() => setIsCustomOffcanvasOpen(false)}
        direction={customOffcanvasConfig.direction}
        backdrop={customOffcanvasConfig.backdrop}
        scrollable={customOffcanvasConfig.scrollable}
        width="28vw"
      >
        <TeamCanvasContent toggle={() => setIsCustomOffcanvasOpen(false)} />
      </CustomOffcanvas>
    </div>
  );
}
